/**
 * One view row's **canonical** settings, plus the save that writes them back
 * (`specs/M4-V2-kanban.md` §4, §9 S2).
 *
 * The row already travels with the tabs bar (`views.list` returns `settings`), so this adds
 * no read. Writing goes through `views.update`, whose service normalises the patch over the
 * kind's defaults and validates it — the browser never has to send a whole settings object.
 */
import { viewTypeRegistry } from '@elizmonday/shared';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useBoardInvalidation, useBoardViews } from '../../boards/hooks';
import { useTRPCClient } from '../../../lib/trpc';

export interface ViewSettingsHandle<TSettings> {
  readonly settings: TSettings;
  /** `false` while the tabs are still loading — the caller shows a skeleton, not defaults. */
  readonly ready: boolean;
  /** The view row's name, for the settings pane's heading. */
  readonly name: string;
  save(patch: Partial<TSettings>): void;
  readonly saving: boolean;
}

/**
 * `override` is how the `/__ui/views` harness runs the three renderers with **no session, no
 * api and no database** — the same trick B4's board harness uses. The hooks still run (a
 * conditional hook is not a thing), the query is simply disabled.
 */
export interface ViewSettingsOverride<TSettings> {
  settings: TSettings;
  save(patch: Partial<TSettings>): void;
}

export function useViewSettings<TSettings extends object>(
  boardId: string,
  viewId: string,
  registryId: string,
  override?: ViewSettingsOverride<TSettings>,
): ViewSettingsHandle<TSettings> {
  const views = useBoardViews(override === undefined ? boardId : null);
  const client = useTRPCClient();
  const invalidate = useBoardInvalidation();
  /**
   * The **optimistic overlay**. The pane's inputs are controlled by these settings, so without
   * it a checkbox stays in its old state for the whole `views.update` + refetch round trip and
   * the click looks lost — which is exactly what `views-{kanban,calendar,gallery}.spec.ts`
   * caught the first time they were executed (`locator.check: Clicking the checkbox did not
   * change its state`). monday's pane answers immediately; ours now does too, and the overlay
   * is dropped once the refetched row carries the same value.
   */
  const [pending, setPending] = useState<Partial<TSettings> | null>(null);

  const definition = viewTypeRegistry.find(registryId);
  const row = views.data?.find((entry) => entry.id === viewId);

  const stored = useMemo(() => {
    const defaults = (definition?.defaultSettings ?? {}) as TSettings;
    if (row === undefined) return defaults;
    // `normaliseSettings` is the kind's own merge-and-validate; a row written before the kind
    // was built, or by the importer, is therefore read with today's shape and no migration.
    try {
      return (definition?.normaliseSettings?.(row.settings) ?? row.settings ?? defaults) as TSettings;
    } catch {
      return defaults;
    }
  }, [definition, row]);

  const settings = useMemo(
    () => (pending === null ? stored : ({ ...stored, ...pending } as TSettings)),
    [stored, pending],
  );

  const mutation = useMutation({
    mutationFn: (patch: Partial<TSettings>) =>
      client.views.update.mutate({
        boardId,
        viewId,
        settings: { ...settings, ...patch } as Record<string, unknown>,
      }),
    onSuccess: async () => {
      await invalidate();
    },
    onError: () => setPending(null),
  });

  /**
   * The overlay is dropped **per key, once the refetched row carries that value** — which is what
   * the comment above always claimed and the code did not do. It used to clear the whole overlay
   * in `onSuccess`, and `views.update` *replaces* the settings object (`services/views.ts`
   * normalises the patch over the kind's defaults, it does not merge into the row), so a second
   * save fired in the window between "the first one succeeded" and "its row has arrived" sent the
   * **pre-first-save** value for every other key and silently rolled it back.
   *
   * Round 10j's P-147 made that window easy to hit — the gantt's zoom controls save now, and a
   * user picks a zoom right after toggling something in the pane — and the C3 verifier caught it
   * as *Show dependencies* turning itself off (`G6 · changing the zoom does not roll back another
   * setting`).
   */
  useEffect(() => {
    setPending((current) => {
      if (current === null) return null;
      const unsettled = Object.entries(current).filter(
        ([key, value]) => JSON.stringify((stored as Record<string, unknown>)[key]) !== JSON.stringify(value),
      );
      if (unsettled.length === Object.keys(current).length) return current;
      return unsettled.length === 0 ? null : (Object.fromEntries(unsettled) as Partial<TSettings>);
    });
  }, [stored]);

  if (override !== undefined) {
    return { settings: override.settings, ready: true, name: registryId, save: override.save, saving: false };
  }
  return {
    settings,
    ready: views.data !== undefined,
    name: row?.name ?? '',
    save: (patch) => {
      setPending((previous) => ({ ...(previous ?? {}), ...patch }));
      mutation.mutate(patch);
    },
    saving: mutation.isPending,
  };
}
